'use client';

import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, RefreshCw, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomUuidGeneratorContent } from '../../data/random-uuid-generator-content';
import Link from 'next/link';

type UUIDFormat = 'lowercase' | 'uppercase' | 'braces' | 'urn' | 'base64' | 'number';

export default function RandomUUIDGenerator() {
    const [uuid, setUuid] = useState<string>('');
    const [format, setFormat] = useState<UUIDFormat>('lowercase');
    const [count, setCount] = useState<number>(1);
    const [copied, setCopied] = useState<boolean>(false);
    const { toast } = useToast();

    const generateRandomHex = useCallback(() => {
        return Math.floor(Math.random() * 16).toString(16);
    }, []);

    const generateUUID = useCallback(() => {
        // Generate a version 4 UUID (random)
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }, []);

    const formatUUID = useCallback((uuid: string): string => {
        switch (format) {
            case 'uppercase':
                return uuid.toUpperCase();
            case 'braces':
                return `{${uuid}}`;
            case 'urn':
                return `urn:uuid:${uuid}`;
            case 'base64':
                return btoa(uuid);
            case 'number':
                return uuid.replace(/[^0-9]/g, '');
            default:
                return uuid.toLowerCase();
        }
    }, [format]);

    const generateNewUUID = useCallback(() => {
        const newUuid = generateUUID();
        setUuid(formatUUID(newUuid));
        setCopied(false);
    }, [generateUUID, formatUUID]);

    const generateMultipleUUIDs = useCallback(() => {
        const uuids = [];
        for (let i = 0; i < count; i++) {
            uuids.push(formatUUID(generateUUID()));
        }
        return uuids.join('\n');
    }, [count, generateUUID, formatUUID]);

    const copyToClipboard = useCallback((text: string) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'UUID has been copied to clipboard.',
        });
        setTimeout(() => setCopied(false), 2000);
    }, [toast]);

    useEffect(() => {
        generateNewUUID();
    }, [generateNewUUID]);

    useEffect(() => {
        // Update the displayed UUID when format changes
        if (uuid) {
            const baseUuid = uuid.replace(/[{}]/g, '').replace('urn:uuid:', '').toLowerCase();
            setUuid(formatUUID(baseUuid));
        }
    }, [format, formatUUID]);

    return (
        <div className="container mx-auto px-4 py-8">
            <ToolPageIntro title="Random UUID Generator">
                Generate random UUIDs with our free online tool. Create UUID v4, v1, and custom identifiers for databases, applications, and unique identification.
            </ToolPageIntro>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>UUID Generator</CardTitle>
                            <CardDescription>Generate random UUIDs (version 4)</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center space-x-2">
                                <Input
                                    value={uuid}
                                    readOnly
                                    className="text-lg font-mono flex-1"
                                />
                                <Button
                                    size="icon"
                                    variant="outline"
                                    onClick={() => copyToClipboard(uuid)}
                                    aria-label="Copy to clipboard"
                                >
                                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                </Button>
                                <Button onClick={generateNewUUID} variant="outline">
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Generate
                                </Button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <Label htmlFor="format" className="block mb-2">Format</Label>
                                    <select
                                        id="format"
                                        value={format}
                                        onChange={(e) => setFormat(e.target.value as UUIDFormat)}
                                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <option value="lowercase">Lowercase (xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx)</option>
                                        <option value="uppercase">Uppercase (XXXXXXXX-XXXX-4XXX-YXXX-XXXXXXXXXXXX)</option>
                                        <option value="braces">With Braces (&#123;xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx&#125;)</option>
                                        <option value="urn">URN (urn:uuid:xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx)</option>
                                        <option value="base64">Base64 Encoded</option>
                                        <option value="number">Numbers Only</option>
                                    </select>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Multiple UUIDs</CardTitle>
                            <CardDescription>Generate multiple UUIDs at once</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="flex items-center space-x-2">
                                    <div className="flex-1">
                                        <Label htmlFor="count" className="block mb-2">Number of UUIDs (1-100)</Label>
                                        <Input
                                            id="count"
                                            type="number"
                                            min="1"
                                            max="100"
                                            value={count}
                                            onChange={(e) => setCount(Math.min(Math.max(1, parseInt(e.target.value) || 1), 100))}
                                            className="w-24"
                                        />
                                    </div>
                                    <Button
                                        onClick={() => copyToClipboard(generateMultipleUUIDs())}
                                        className="mt-6"
                                        variant="outline"
                                    >
                                        <Copy className="mr-2 h-4 w-4" />
                                        Copy All
                                    </Button>
                                </div>
                                <div className="bg-muted p-4 rounded-md">
                                    <pre className="text-sm overflow-auto max-h-60">
                                        {generateMultipleUUIDs()}
                                    </pre>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>About UUIDs</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <p>
                                A UUID (Universally Unique Identifier) is a 128-bit number used to uniquely identify information in computer systems.
                            </p>
                            <div className="space-y-2">
                                <h3 className="font-semibold">UUID Version 4 (Random)</h3>
                                <p className="text-sm">
                                    This generator creates version 4 UUIDs, which are randomly generated. The chance of a collision is extremely low.
                                </p>
                            </div>
                            <div className="space-y-2">
                                <h3 className="font-semibold">Common Uses:</h3>
                                <ul className="list-disc pl-5 space-y-1 text-sm">
                                    <li>Database primary keys</li>
                                    <li>Session identifiers</li>
                                    <li>Transaction IDs</li>
                                    <li>Unique filenames</li>
                                    <li>Distributed systems</li>
                                </ul>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Formats</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            <div className="space-y-1">
                                <p className="text-sm font-medium">Lowercase (default)</p>
                                <p className="text-xs font-mono bg-muted p-2 rounded">550e8400-e29b-41d4-a716-446655440000</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-sm font-medium">Uppercase</p>
                                <p className="text-xs font-mono bg-muted p-2 rounded">550E8400-E29B-41D4-A716-446655440000</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-sm font-medium">With Braces</p>
                                <p className="text-xs font-mono bg-muted p-2 rounded">&#123;550e8400-e29b-41d4-a716-446655440000&#125;</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-sm font-medium">URN Format</p>
                                <p className="text-xs font-mono bg-muted p-2 rounded">urn:uuid:550e8400-e29b-41d4-a716-446655440000</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomUuidGeneratorContent.title}
                description={randomUuidGeneratorContent.description}
                content={randomUuidGeneratorContent.content}
                faqs={randomUuidGeneratorContent.faqs}
                relatedTools={randomUuidGeneratorContent.relatedTools}
            />
        </div>
    );
}
